import { usePageHeader } from '../../../../utils/hooks'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { AnalysisParameters } from './components/AnalysisParameters/AnalysisParameters'
import { useQuery } from 'react-query'
import { useMemo, useState } from 'react'
import { useDeliveriesService } from 'services'
import dayjs from 'dayjs'
import { DeliveryHeader } from './components/DeliveryHeader/DeliveryHeader'
import {
  ChartsContainer,
  CustomChartContainer,
  DeliveryBody,
  StatusContainer,
  TitleContainer,
  TitleSection,
} from './StoredDeliveriesDetails.styled'
import { ChartExportWrapper, CustomDistanceChart } from '../../../../components'
import { DetailsPageHeader } from '../../components/DetailsPageHeader/DetailsPageHeader'
import { DeliveryStatus } from '../../../../components/features/DeliveryStatus/DeliveryStatus'

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
`

const MainContent = styled(Box)`
  display: flex;
  gap: 16px;
  flex: 1;
`

const MainContentHeading = styled(Box)`
  width: 100%;
  display: flex;
  border-bottom: 1px solid #c93c41;
  flex-direction: column;
  padding-bottom: 10px;
`

const Title = styled(Typography)`
  font-size: 18px;
  font-weight: 900;
`

const LoaderBox = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`

export const StoredDeliveriesDetails = () => {
  const navigate = useNavigate()
  const { getDeliveryById, getSpectralDistanceData } = useDeliveriesService()

  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'))

  const { wheatCodeId, deliveryId } = useParams<{
    wheatCodeId: string
    deliveryId: string
  }>()
  const [activeDeliveryId, setActiveDeliveryId] = useState<number>(
    Number(deliveryId)
  )

  const { data: deliveryItem, isLoading: deliveryItemIsLoading } = useQuery({
    queryKey: ['delivery_by_id', deliveryId],
    queryFn: () => getDeliveryById(deliveryId),
    retry: false,
  })

  const headerMemo = useMemo(
    () => ({
      headerTitle: (
        <TitleSection>
          <Typography fontWeight="700" fontSize={20}>
            Wheat code {deliveryItem?.result?.wheat_code?.code || ''}
          </Typography>
          {!isMediumScreen && deliveryItem?.result && (
            <DetailsPageHeader delivery={deliveryItem?.result} />
          )}
          <Stack width="200px" />
        </TitleSection>
      ),
      showGoBack: true,
      goBackUrl: '/shipments',
    }),
    [isMediumScreen, deliveryItem?.result]
  )

  usePageHeader(headerMemo)

  const onClick = (deliveryId: number, wheatCodeId: string) => {
    setActiveDeliveryId(deliveryId)
    navigate(`/shipments/stored-deliveries/${deliveryId}/${wheatCodeId}`)
  }

  const NirDate = useMemo(
    () => deliveryItem?.result?.nir?.arrival_date,
    [deliveryItem?.result?.nir?.arrival_date]
  )

  const AcceptedBin = deliveryItem?.result?.suggested_bins?.find(
    (item) => item.bin_accepted === true
  )

  const {
    data: spectralDistanceChartData,
    isLoading: spectralDistanceChartDataIsLoading,
  } = useQuery(
    ['spectralDistance', deliveryId],
    () => (deliveryId ? getSpectralDistanceData(deliveryId) : null),
    {
      enabled: !!deliveryId,
      select: (res) => res?.result,
    }
  )

  const mappedChartData = spectralDistanceChartData
    ? [
        ...(spectralDistanceChartData?.historical_spectral_distances?.map(
          (historicData) => ({
            threshold: spectralDistanceChartData.threshold_line,
            point: historicData.point,
            date: historicData.arrival_date,
          })
        ) || []),
        {
          point: spectralDistanceChartData.point,
          threshold: spectralDistanceChartData.threshold_line,
          date: spectralDistanceChartData.arrival_date,
          current: true,
        },
      ]
    : []

  return (
    <Container>
      {deliveryItemIsLoading || spectralDistanceChartDataIsLoading ? (
        <LoaderBox>
          <CircularProgress />
        </LoaderBox>
      ) : (
        <>
          <DeliveryHeader
            activeDeliveryId={activeDeliveryId}
            onDeliveryClick={onClick}
            wheatCodeId={wheatCodeId}
            wheatCode={deliveryItem?.result?.wheat_code?.code}
          />
          <DeliveryBody>
            <TitleContainer>
              <MainContentHeading>
                <Title>
                  {dayjs(NirDate).format('DD.MM.YY')} :{' '}
                  {dayjs(NirDate).format('HH:mm')}
                </Title>
              </MainContentHeading>
            </TitleContainer>
            <MainContent
              sx={{
                flexDirection: isMediumScreen ? 'column' : 'row',
              }}
            >
              <AnalysisParameters
                delivery={deliveryItem?.result}
                binName={AcceptedBin?.bin?.name}
              />
              <ChartsContainer>
                <CustomChartContainer>
                  {mappedChartData.length > 0 && (
                    <ChartExportWrapper
                      title="Spectral Distance"
                      actionsTopPadding={-24}
                      actionsRightPadding={32}
                    >
                      <CustomDistanceChart data={mappedChartData} />
                    </ChartExportWrapper>
                  )}
                </CustomChartContainer>
                <StatusContainer>
                  <DeliveryStatus {...deliveryItem?.result?.consistency} />
                </StatusContainer>
              </ChartsContainer>
            </MainContent>
          </DeliveryBody>
        </>
      )}
    </Container>
  )
}
